// ── Tab: Mantenimiento ────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Loader, AlertCircle, Check, X, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../services/apiService';

const TYPE_OPTIONS = [
  { value: 'warning',     label: '⚠️ Aviso',           color: '#d97706' },
  { value: 'maintenance', label: '🔧 Mantenimiento',    color: '#dc2626' },
  { value: 'info',        label: 'ℹ️ Información',      color: '#2563eb' },
];

const TabMaintenance = ({ orgs, darkMode }) => {
  const [notices, setNotices]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [deleteModal, setDeleteModal] = useState(null); // notice a eliminar
  const [deleting, setDeleting]     = useState(false);

  const [form, setForm] = useState({
    message:    '',
    type:       'warning',
    target_org: '',
    ends_at:    '',
  });

  const text = darkMode ? 'text-white' : 'text-gray-900';
  const sub  = darkMode ? 'text-gray-400' : 'text-gray-500';
  const card = `rounded-2xl border transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/maintenance');
      setNotices(res.data.data || []);
    } catch { setError('Error al cargar los avisos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.message.trim()) { setError('El mensaje es requerido'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/api/maintenance', {
        message:    form.message.trim(),
        type:       form.type,
        target_org: form.target_org || undefined,
        ends_at:    form.ends_at    || undefined,
      });
      setForm({ message: '', type: 'warning', target_org: '', ends_at: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear el aviso');
    } finally { setSaving(false); }
  };

  const handleToggle = async (notice) => {
    try {
      await api.put(`/api/maintenance/${notice.id}`, { is_active: !notice.is_active });
      await load();
    } catch { setError('Error al actualizar'); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/api/maintenance/${deleteModal.id}`);
      setDeleteModal(null);
      await load();
    } catch { setError('Error al eliminar'); }
    finally { setDeleting(false); }
  };

  const typeInfo = (type) => TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-black ${text}`}>Avisos de Mantenimiento</h2>
          <p className={`text-sm mt-1 ${sub}`}>Enviá mensajes a todos los aeroclubes o a uno específico</p>
        </div>
        <motion.button onClick={() => setShowForm(p => !p)}
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', borderRadius: 14, border: 'none', cursor: 'pointer', background: showForm ? 'rgba(220,38,38,0.1)' : 'linear-gradient(135deg,#dc2626,#b91c1c)', color: showForm ? '#dc2626' : 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit' }}>
          {showForm ? <><X style={{ width: 16, height: 16 }} /> Cancelar</> : <><Plus style={{ width: 16, height: 16 }} /> Nuevo aviso</>}
        </motion.button>
      </div>

      {/* Formulario */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className={`${card} p-5 space-y-4`}>
            <h3 className={`font-bold text-base ${text}`}>Crear nuevo aviso</h3>

            <div>
              <label className={`block text-xs font-bold uppercase tracking-wide mb-1.5 ${sub}`}>Mensaje *</label>
              <textarea
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Ej: Vamos a realizar un mantenimiento programado. Ciertas funcionalidades podrían estar afectadas durante unos minutos."
                rows={3}
                style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: darkMode ? 'white' : '#0f172a', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wide mb-1.5 ${sub}`}>Tipo</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: darkMode ? 'white' : '#0f172a', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                  {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-bold uppercase tracking-wide mb-1.5 ${sub}`}>Aeroclub (vacío = todos)</label>
                <select value={form.target_org} onChange={e => setForm(f => ({ ...f, target_org: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: darkMode ? 'white' : '#0f172a', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="">Todos los aeroclubes</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={`block text-xs font-bold uppercase tracking-wide mb-1.5 ${sub}`}>Vencimiento (opcional)</label>
              <input type="datetime-local" value={form.ends_at} onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: darkMode ? 'white' : '#0f172a', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
              <span className={`text-xs mt-1 block ${sub}`}>Si no ponés fecha, el aviso se muestra hasta que lo desactivés manualmente</span>
            </div>

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle style={{ width: 14, height: 14, color: '#ef4444', flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 13, color: '#ef4444' }}>{error}</p>
              </div>
            )}

            <motion.button onClick={handleCreate} disabled={saving}
              whileHover={!saving ? { scale: 1.02 } : {}} whileTap={!saving ? { scale: 0.97 } : {}}
              style={{ width: '100%', padding: 13, background: saving ? 'rgba(220,38,38,0.4)' : 'linear-gradient(135deg,#dc2626,#b91c1c)', color: 'white', border: 'none', borderRadius: 14, fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontFamily: 'inherit' }}>
              {saving ? <><Loader style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} />Publicando...</> : <><Check style={{ width: 15, height: 15 }} />Publicar aviso</>}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de avisos */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader style={{ width: 28, height: 28, color: '#dc2626', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : notices.length === 0 ? (
        <div className={`${card} text-center py-12`}>
          <p style={{ fontSize: 36, marginBottom: 10 }}>📢</p>
          <p className={`font-medium ${sub}`}>No hay avisos creados</p>
          <p className={`text-xs mt-1 ${sub}`}>Creá uno para que aparezca en la app de todos los aeroclubes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notices.map((notice, i) => {
            const tInfo   = typeInfo(notice.type);
            const expired = notice.ends_at && new Date(notice.ends_at) < new Date();
            return (
              <motion.div key={notice.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className={`${card} p-4`}
                style={{ opacity: (!notice.is_active || expired) ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flexShrink: 0, marginTop: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: tInfo.color + '15', color: tInfo.color, border: `1px solid ${tInfo.color}30` }}>
                      {tInfo.label}
                    </span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: darkMode ? 'white' : '#0f172a', lineHeight: 1.5 }}>
                      {notice.message}
                    </p>
                    <div style={{ display: 'flex', gap: 12, marginTop: 5, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
                        👤 {notice.created_by_name || 'SuperAdmin'}
                      </span>
                      <span style={{ fontSize: 11, color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
                        🏢 {notice.org_name || 'Todos los aeroclubes'}
                      </span>
                      {notice.ends_at && (
                        <span style={{ fontSize: 11, color: expired ? '#ef4444' : (darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8') }}>
                          {expired ? '⏱ Vencido' : `⏰ Vence: ${new Date(notice.ends_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}`}
                        </span>
                      )}
                      <span style={{ fontSize: 11, fontWeight: 700, color: notice.is_active && !expired ? '#16a34a' : '#64748b' }}>
                        {notice.is_active && !expired ? '● Activo' : '○ Inactivo'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <button onClick={() => handleToggle(notice)} title={notice.is_active ? 'Desactivar' : 'Activar'}
                      style={{ padding: '6px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'transparent', color: notice.is_active ? '#16a34a' : '#64748b' }}>
                      {notice.is_active
                        ? <ToggleRight style={{ width: 20, height: 20 }} />
                        : <ToggleLeft  style={{ width: 20, height: 20 }} />
                      }
                    </button>
                    <button onClick={() => setDeleteModal(notice)} title="Eliminar"
                      style={{ padding: '6px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'transparent', color: '#ef4444' }}>
                      <Trash2 style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Modal de confirmación de eliminación ── */}
      <AnimatePresence>
        {deleteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => !deleting && setDeleteModal(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ width: '100%', maxWidth: 380, borderRadius: 28, padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', background: darkMode ? '#1e293b' : 'white' }}>

              {/* Ícono */}
              <div style={{ width: 56, height: 56, borderRadius: 18, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Trash2 style={{ width: 26, height: 26, color: '#ef4444' }} />
              </div>

              {/* Título */}
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, textAlign: 'center', color: darkMode ? 'white' : '#0f172a' }}>
                ¿Eliminar aviso?
              </h3>

              {/* Preview del mensaje */}
              <div style={{ margin: '12px 0 20px', padding: '10px 14px', borderRadius: 12, background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}>
                <p style={{ margin: 0, fontSize: 13, color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b', lineHeight: 1.5, fontStyle: 'italic' }}>
                  "{deleteModal.message.length > 80 ? deleteModal.message.slice(0, 80) + '...' : deleteModal.message}"
                </p>
              </div>

              <p style={{ margin: '0 0 24px', fontSize: 13, textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
                Esta acción no se puede deshacer.
              </p>

              {/* Botones */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setDeleteModal(null)} disabled={deleting}
                  style={{ flex: 1, padding: '12px', borderRadius: 14, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
                  Cancelar
                </button>
                <motion.button onClick={handleDeleteConfirm} disabled={deleting}
                  whileHover={!deleting ? { scale: 1.02 } : {}} whileTap={!deleting ? { scale: 0.97 } : {}}
                  style={{ flex: 1, padding: '12px', borderRadius: 14, border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, color: 'white', background: 'linear-gradient(135deg,#ef4444,#dc2626)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  {deleting
                    ? <><Loader style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> Eliminando...</>
                    : <><Trash2 style={{ width: 15, height: 15 }} /> Eliminar</>
                  }
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default TabMaintenance;
